import { Tooltip } from '@/components';
import { Card } from '@/components/Card';
import { Container } from '@/components/Container';
import { PageDetailsHeaderRow } from '@/components/PageDetailsHeaderRow/PageDetailsHeaderRow';
import { StatusCard } from '@/components/StatusCard/StatusCard';
import { EditWorkflowModal } from '@/modules/Workflows/components/EditWorkflowModal';
import { WorkflowModel } from '@/modules/Workflows/domain';
import { formatNumber, secondsToTimeDurationString } from '@/utils';
import { SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HeaderRowProps {
  workflow?: WorkflowModel;
  handleStatus: (
    checked: boolean,
    event: MouseEvent | SyntheticEvent<MouseEvent | KeyboardEvent>,
    id: string,
  ) => void;
  status: boolean;
}

export const WorkflowHeaderRow = ({
  workflow,
  handleStatus,
  status,
}: HeaderRowProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const handleModalState = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  return (
    <div>
      <PageDetailsHeaderRow
        isLoading={!workflow}
        title={workflow?.name ?? ''}
        description={
          <div className="text-sm">
            <span className="font-bold" data-testid="ba-comp-section">
              {`${workflow?.businessAreaName}, ${workflow?.company}, `}
            </span>
            <span>{workflow?.organization}</span>
          </div>
        }
        actions={<StatusCard status={status} handleStatus={handleStatus} />}
        onEditClick={handleModalState}
        details={
          <div className="flex flex-col gap-1">
            <Container className="flex-row">
              <Card title={t('workflow_description')}>
                {<p>{workflow?.description}</p>}
              </Card>
              <Card title={t('code')}>{<p>{workflow?.code}</p>}</Card>
            </Container>
            <Container className="flex-row">
              <Card title={t('total_users')}>
                <p data-testid="total-users-section">{workflow?.totalUsers}</p>
              </Card>
              <Card title={t('workload_monthly')}>
                <p data-testid="workload-section">
                  {secondsToTimeDurationString(
                    (workflow?.totalWorkload ?? 0) * 60,
                  )}
                </p>
              </Card>
              <Card title={t('estimated_cost_monthly')}>
                <Tooltip
                  containerClassName="w-fit"
                  showArrow
                  content={`${formatNumber(workflow?.totalEstimatedCost ?? 0, 4)}€`}
                >
                  {formatNumber(workflow?.totalEstimatedCost ?? 0)}€
                </Tooltip>
              </Card>
            </Container>
          </div>
        }
      />
      {isOpen && workflow?.id && (
        <EditWorkflowModal
          isOpen={isOpen}
          id={workflow?.id}
          handleClose={handleModalState}
        />
      )}
    </div>
  );
};
